/**
 * Desktop implementation of `Platform`: a thin adapter over the preload bridge.
 *
 * All of the real work (markdown files on disk, the settings file, the window)
 * happens in the main process, so there is deliberately no logic here.
 */

import type { JotBridge } from '@shared/ipc'
import type { Platform } from '@shared/types'

export async function createElectronPlatform(bridge: JotBridge): Promise<Platform> {
  // The only value worth fetching eagerly: it never changes for the lifetime of
  // the window, and the Settings dialog wants it synchronously.
  const info = await bridge.info()

  // The bridge sub-objects are re-wrapped rather than handed over directly:
  // they carry `onChanged` members that are not part of `NotesStore` /
  // `SettingsStore`, and calls have to keep their `contextBridge` receiver.
  const platform: Platform = {
    info,

    notes: {
      list: () => bridge.notes.list(),
      get: (id) => bridge.notes.get(id),
      create: (body) => bridge.notes.create(body),
      update: (id, body) => bridge.notes.update(id, body),
      remove: (id) => bridge.notes.remove(id),
    },

    settings: {
      get: () => bridge.settings.get(),
      set: (patch) => bridge.settings.set(patch),
    },

    syncState: {
      get: () => bridge.syncState.get(),
      set: (patch) => bridge.syncState.set(patch),
    },

    window: {
      hide: () => bridge.window.hide(),
      quit: () => bridge.window.quit(),
      openNotesFolder: () => bridge.window.openNotesFolder(),
    },

    onSettingsChanged: (listener) => bridge.settings.onChanged(listener),
    onNotesChanged: (listener) => bridge.notes.onChanged(listener),
  }

  return platform
}
